import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  CalendarDays,
  Bell,
  ShieldCheck,
  User,
  LogOut,
  Home,
  Users2,
  Contact,
  Truck,
  Settings,
  History,
  ChevronDown,
  Plus,
  Menu,
  Award,
  TrendingUp,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { useGuardedNavigate, useConfirmIfDirty } from "@/context/UnsavedChangesContext";
import { api } from "@/lib/api";
import { startAutoPush } from "@/lib/push";
import { AltarodaLogo } from "@/components/Logo";
import { SHELL } from "@/constants/testIds";
import { fmtRoleLabel } from "@/lib/format";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  UI_MODE_DEFAULT, UI_MODE_OPERATOR, useUiMode, useHomePath, useModePending,
  usePerformanceEnabled,
} from "@/lib/uiMode";

// P0.3 — navegação declarativa por modo, em vez de JSX condicional espalhado.
// `roles` restringe um item a perfis; `badge: "unread"` liga-o ao contador de
// notificações; `flag` exige uma feature flag ligada (ver `navFlags`).
// A secção de Administração continua a ser JSX (é um bloco com cabeçalho e
// regras próprias) e só existe no modo default.
//
// O modo `operator` só é atingível com a flag `operator_mode_enabled` ligada
// (ver `lib/uiMode.js`); com a flag desligada — o estado actual — todos os
// perfis usam `default` e a navegação é exactamente a de hoje.
const NAV_CONFIG = {
  [UI_MODE_DEFAULT]: {
    adminSection: true,
    sidebar: [
      // Auditoria 2026-10 — a casa do operador é "O meu dia" mesmo com a flag
      // desligada; o Dashboard dele continua lá, como "Os meus indicadores".
      { to: "/hoje", icon: Home, label: "O meu dia", testId: SHELL.navHoje, roles: ["operador"] },
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testId: SHELL.navDashboard, roles: ["admin", "gestor"] },
      { to: "/dashboard", icon: LayoutDashboard, label: "Os meus indicadores", testId: SHELL.navDashboard, roles: ["operador"] },
      { to: "/pedidos", icon: ClipboardList, label: "Pedidos", testId: SHELL.navPedidos },
      { to: "/agenda", icon: CalendarDays, label: "Agenda", testId: SHELL.navAgenda },
      { to: "/clientes", icon: Contact, label: "Clientes", testId: SHELL.navClientes, roles: ["admin", "gestor", "operador"] },
      { to: "/fornecedores", icon: Truck, label: "Fornecedores", testId: SHELL.navFornecedores, roles: ["admin", "gestor", "operador"] },
      { to: "/notificacoes", icon: Bell, label: "Notificações", testId: SHELL.navNotificacoes, badge: "unread" },
      { to: "/evolucao", icon: Award, label: "A minha evolução", testId: SHELL.navReconhecimento, flag: "performance" },
    ],
    bottom: [
      { to: "/dashboard", icon: Home, label: "Início", testId: SHELL.navDashboard, roles: ["admin", "gestor"] },
      { to: "/hoje", icon: Home, label: "Início", testId: SHELL.navHoje, roles: ["operador"] },
      { to: "/pedidos", icon: ClipboardList, label: "Pedidos", testId: SHELL.navPedidos },
      // H3.20 — Perfil sai da bottom nav (continua acessível via o menu de
      // utilizador no header); o espaço passa a ser ocupado por Agenda.
      { to: "/agenda", icon: CalendarDays, label: "Agenda", testId: SHELL.navAgenda },
      { to: "/notificacoes", icon: Bell, label: "Notificações", testId: SHELL.navNotificacoes, badge: "unread" },
    ],
  },
  [UI_MODE_OPERATOR]: {
    adminSection: false,
    sidebar: [
      { to: "/hoje", icon: Home, label: "Hoje", testId: SHELL.navHoje },
      { to: "/agenda", icon: CalendarDays, label: "Agenda", testId: SHELL.navAgenda },
      { to: "/notificacoes", icon: Bell, label: "Avisos", testId: SHELL.navNotificacoes, badge: "unread" },
      { to: "/evolucao", icon: Award, label: "A minha evolução", testId: SHELL.navReconhecimento, flag: "performance" },
    ],
    bottom: [
      { to: "/hoje", icon: Home, label: "Hoje", testId: SHELL.navHoje },
      { to: "/agenda", icon: CalendarDays, label: "Agenda", testId: SHELL.navAgenda },
      { to: "/notificacoes", icon: Bell, label: "Avisos", testId: SHELL.navNotificacoes, badge: "unread" },
    ],
  },
};

// Modo ainda por decidir: nem navegação nem atalhos de criação. Ver o
// comentário em `AppShell` sobre `modePending`.
const NAV_PENDING = { adminSection: false, sidebar: [], bottom: [] };

function visibleItems(items, role, flags = {}) {
  return items.filter(
    (it) => (!it.roles || it.roles.includes(role)) && (!it.flag || flags[it.flag] === true)
  );
}

function SideItem({ to, icon: Icon, label, testId, exact = false, badge, onClick }) {
  return (
    <NavLink
      to={to}
      end={exact}
      data-testid={testId}
      onClick={onClick}
      className={({ isActive }) =>
        [
          "group flex items-center gap-3 rounded-[10px] px-3 py-2 text-sm font-medium transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-1 focus-visible:ring-offset-[#171717]",
          isActive
            ? "bg-white/5 text-white"
            : "text-white/70 hover:text-white hover:bg-white/[0.04]",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          <span
            className="inline-block w-[3px] h-5 rounded-full shrink-0"
            style={{
              backgroundColor: isActive ? "#CD013A" : "transparent",
            }}
            aria-hidden="true"
          />
          <Icon size={20} strokeWidth={1.5} className="shrink-0" />
          <span className="flex-1 truncate">{label}</span>
          {badge > 0 && (
            <span
              data-testid={`${testId}-badge`}
              className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold text-white"
              style={{ backgroundColor: "#CD013A" }}
            >
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function BottomItem({ to, icon: Icon, label, testId, exact = false, badge }) {
  return (
    <NavLink
      to={to}
      end={exact}
      data-testid={testId}
      className={({ isActive }) =>
        [
          // Larger tap area (min 56px height) + comfortable spacing.
          "flex-1 min-w-0 flex flex-col items-center justify-center gap-1 py-2.5 relative select-none",
          // Inactive state uses a darker gray than the site-wide
          // `ink-secondary` token (#6B7280) specifically here: at the small
          // 26px icon size, thin-stroke glyphs (esp. Bell) read as
          // washed-out/disabled on Android screens even though the token
          // passes contrast on paper. #4B5563 keeps it clearly distinct
          // from the active red without looking disabled. Applied to all 4
          // items uniformly, not just Notificações, to stay visually
          // consistent across the bar.
          isActive ? "text-ink-primary" : "text-[#4B5563]",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          <div className="relative">
            <Icon
              size={26}
              strokeWidth={1.75}
              color={isActive ? "#CD013A" : undefined}
            />
            {badge > 0 && (
              <span
                data-testid={`${testId}-badge`}
                className="absolute -top-1.5 -right-2.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-semibold text-white"
                style={{ backgroundColor: "#CD013A" }}
              >
                {badge > 9 ? "9+" : badge}
              </span>
            )}
          </div>
          <span className="text-[12px] font-medium leading-none tracking-tight">{label}</span>
          {isActive && (
            <span
              className="absolute top-0 h-[3px] w-8 rounded-full"
              style={{ backgroundColor: "#CD013A" }}
              aria-hidden="true"
            />
          )}
        </>
      )}
    </NavLink>
  );
}

// Superfícies exclusivamente de leitura, onde o FAB de criar pedido não entra.
// Só se nota em admin e gestor: o modo operador nunca teve FAB.
const ANALYTIC_PATHS = ["/evolucao", "/equipa/adocao"];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const guardedNavigate = useGuardedNavigate();
  const confirmIfDirty = useConfirmIfDirty();
  const location = useLocation();
  const { unread } = useNotifications();
  const mode = useUiMode();
  // Notificações push ativas por defeito: subscreve em silêncio se a
  // permissão já existe, senão pede-a no primeiro toque (ver lib/push.js).
  useEffect(() => startAutoPush(), []);
  // P0.6 — enquanto a config não chegou, o modo ainda não está decidido. Não
  // basta guardar as ROTAS: se a navegação se pintasse já, um operador via
  // por instantes "Dashboard | Pedidos" antes de a barra trocar para
  // "Hoje | Agenda | Avisos". Aqui a navegação simplesmente não aparece —
  // mostrar a errada durante um segundo é pior do que não mostrar nenhuma.
  // Só o operador espera; para gestor e admin isto é sempre falso.
  const modePending = useModePending();
  const nav = modePending ? NAV_PENDING : NAV_CONFIG[mode];
  // Flags que escondem itens de navegação. A da evolução é
  // `performance_enabled` — quem não vê o próprio score não tem página para
  // onde ir; `ranking` e `recognition` governam blocos dentro dela e não a
  // navegação. É independente de `operator_mode_enabled`: em modo shadow o
  // item não aparece a ninguém, incluindo admin e gestor, que analisam a
  // adoção em "Equipa → Adoção" (sempre disponível).
  const performanceEnabled = usePerformanceEnabled();
  const navFlags = useMemo(() => ({ performance: performanceEnabled }), [performanceEnabled]);
  const homePath = useHomePath();
  const isAdmin = user?.role === "admin";
  const isGestor = user?.role === "gestor";
  const canAudit =
    isAdmin ||
    (isGestor && user?.permissions?.can_view_audit_log);
  const canCreate = useMemo(() => {
    if (!user) return false;
    if (user.role === "admin" || user.role === "gestor") return true;
    return !!user.permissions?.can_create_request;
  }, [user]);
  const isOperator = mode === UI_MODE_OPERATOR;
  // O logo leva sempre a "casa" — e no modo operador casa é "Hoje", não o
  // Dashboard, que nem sequer está na navegação.
  const homeLabel = homePath === "/hoje" ? "Ir para O meu dia" : "Ir para o Dashboard";
  const onNovoPedido = location.pathname.startsWith("/pedidos/novo");
  // Páginas de leitura: existem para consultar números, não para despachar
  // trabalho. Um botão de criar pedido sobreposto ao conteúdo não serve nenhuma
  // intenção de quem está aqui, e em telemóvel tapa a última linha da tabela.
  const onAnalytics = ANALYTIC_PATHS.some((path) => location.pathname.startsWith(path));
  // Hide FAB while on the "create" screens themselves (avoids visual clash).
  // P0.6 — no modo operador não há FAB de todo: o canto inferior direito é
  // da barra de acções de estado (P0.5) e "+ Pedido" passa para o header,
  // onde está sempre visível e nunca disputa espaço com o conteúdo.
  // Auditoria 2026-10 — no detalhe do pedido o FAB tapava as acções do
  // fundo da página (composer, histórico); criar um pedido novo a partir de
  // outro não é a intenção de quem está ali.
  const onPedidoDetalhe = /^\/pedidos\/(?!novo(\/|$))[^/]+/.test(location.pathname);
  const showFab = canCreate && !onNovoPedido && !onAnalytics && !onPedidoDetalhe && !isOperator && !modePending;
  // P0.6 — o "+ Pedido" do header é do modo operador. Gestor e admin
  // continuam com o FAB de sempre, sem alteração nenhuma ao header.
  const showHeaderNewRequest = isOperator && canCreate && !onNovoPedido;
  const bottomItems = visibleItems(nav.bottom, user?.role, navFlags);
  // Saber de onde veio o pedido serve dois fins distintos: o regresso (a
  // partir da Home o URL fica marcado com `?from=hoje`) e as métricas de
  // criação — aí a origem tem de ser um dos nomes da whitelist de `track.js`
  // ("home", não "hoje"), senão o header `X-UI-Source` nem sequer é enviado.
  const goToNewRequest = () => {
    const fromHoje = location.pathname === "/hoje";
    guardedNavigate(fromHoje ? "/pedidos/novo?from=hoje" : "/pedidos/novo", {
      state: { ui_source: fromHoje ? "home" : "header" },
    });
  };
  // Mobile drawer (menu lateral em telemóvel/tablet portrait).
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fecha o drawer sempre que a rota muda — evita o menu ficar aberto quando
  // navega por atalhos ou back/forward do browser.
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const doLogout = async () => {
    setMobileMenuOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };
  const handleLogout = () => confirmIfDirty(doLogout);

  // Bloco H2.10 — logo clicável em qualquer página autenticada (desktop e
  // mobile) leva sempre ao Dashboard, por navegação SPA (nunca reload
  // completo). No drawer mobile fecha-se também o menu, tal como qualquer
  // outro item de navegação (renderNavItems já faz o mesmo via onNavigate).
  // P0.3 — o destino passa a vir de `homePathFor`. Com a flag desligada é
  // `/dashboard`, exactamente como até aqui.
  const goToDashboard = () => {
    setMobileMenuOpen(false);
    guardedNavigate(homePath);
  };

  // Conteúdo do menu (partilhado entre sidebar fixa e Sheet mobile).
  const renderNavItems = (onNavigate) => (
    <>
      {visibleItems(nav.sidebar, user?.role, navFlags).map((it) => (
        <SideItem
          key={it.to}
          to={it.to}
          icon={it.icon}
          label={it.label}
          testId={it.testId}
          badge={it.badge === "unread" ? unread : undefined}
          onClick={onNavigate}
        />
      ))}
      {nav.adminSection && (isAdmin || isGestor) && (
        <>
          <div className="pt-5 pb-2 px-3 text-[11px] uppercase tracking-widest text-white/40">
            Administração
          </div>
          <SideItem to="/equipa/adocao" icon={TrendingUp} label="Adoção da equipa" testId={SHELL.navAdocao} onClick={onNavigate} />
          {isAdmin && (
            <SideItem to="/admin/utilizadores" icon={Users2} label="Utilizadores" testId={SHELL.navAdminUtilizadores} onClick={onNavigate} />
          )}
          {isAdmin && (
            <SideItem to="/admin/configuracoes" icon={Settings} label="Configurações" testId={SHELL.navAdminConfig} onClick={onNavigate} />
          )}
          {canAudit && (
            <SideItem to="/admin/audit-log" icon={History} label="Log de Auditoria" testId={SHELL.navAdminAudit} onClick={onNavigate} />
          )}
        </>
      )}
    </>
  );

  return (
    <div className="min-h-screen-dvh bg-surface-bg text-ink-primary">
      {/* Desktop sidebar */}
      <aside
        data-testid={SHELL.sidebar}
        className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col text-white"
        style={{ backgroundColor: "#171717" }}
      >
        <div
          data-testid={SHELL.sidebarBrand}
          className="h-16 flex items-center px-5 border-b border-white/5"
        >
          <button
            type="button"
            data-testid={SHELL.logoHomeDesktop}
            aria-label={homeLabel}
            onClick={goToDashboard}
            className="rounded-[6px] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-1 focus-visible:ring-offset-[#171717]"
          >
            <AltarodaLogo variant="light" size={22} />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {renderNavItems()}
        </nav>
        <div className="p-3 border-t border-white/5">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-semibold uppercase">
              {user?.name?.slice(0, 1) || "U"}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">
                {user?.name}
              </div>
              <div className="text-[11px] text-white/50 truncate">
                {fmtRoleLabel(user?.role)} · @{user?.username}
              </div>
            </div>
          </div>
          <button
            data-testid={SHELL.logout}
            onClick={handleLogout}
            className="w-full mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-[10px] text-sm text-white/70 hover:text-white hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-1 focus-visible:ring-offset-[#171717]"
          >
            <LogOut size={18} strokeWidth={1.5} />
            Terminar sessão
          </button>
        </div>
      </aside>

      {/* Mobile drawer (< lg) — menu lateral completo incluindo Terminar
          sessão, para não depender do DropdownMenu no header (que ficava
          intermitentemente tapado pelo FAB e/ou overlays em algumas
          configurações de viewport). */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent
          side="left"
          className="w-72 p-0 border-r-0 text-white"
          style={{ backgroundColor: "#171717" }}
          data-testid="shell-mobile-drawer"
        >
          {/* Revisão H.2 — logo mantém o mesmo tamanho visual (size=22); o
              padding do botão só alarga o alvo clicável para ~44px de
              altura (h-16 = 64px comporta a folga sem alterar o layout). */}
          <SheetHeader className="h-16 px-5 border-b border-white/5 flex flex-row items-center justify-between space-y-0">
            <SheetTitle className="text-white" asChild>
              <button
                type="button"
                data-testid={SHELL.logoHomeMobileDrawer}
                aria-label={homeLabel}
                onClick={goToDashboard}
                className="p-2.5 rounded-[6px] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              >
                <AltarodaLogo variant="light" size={22} />
              </button>
            </SheetTitle>
            <SheetDescription className="sr-only">Menu de navegação</SheetDescription>
          </SheetHeader>
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto max-h-[calc(100vh-8rem)]">
            {renderNavItems(() => setMobileMenuOpen(false))}
          </nav>
          <div className="p-3 border-t border-white/5">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-semibold uppercase">
                {user?.name?.slice(0, 1) || "U"}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-[11px] text-white/50 truncate">
                  {fmtRoleLabel(user?.role)} · @{user?.username}
                </div>
              </div>
            </div>
            <button
              data-testid="shell-mobile-logout"
              onClick={handleLogout}
              className="w-full mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-[10px] text-sm text-white/70 hover:text-white hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              <LogOut size={18} strokeWidth={1.5} />
              Terminar sessão
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Main area */}
      <div className="lg:pl-64">
        {/* Header (mobile & desktop). `viewport-fit=cover` (index.html) faz
            a página estender-se para debaixo do notch/status bar em iOS
            standalone — o padding-top compensa isso sem afectar a altura
            útil do conteúdo (h-14 fica no wrapper interno, não aqui). Em
            Android/desktop env(safe-area-inset-top) é 0, sem efeito. */}
        <header
          data-testid={SHELL.header}
          className="sticky top-0 z-30 bg-white border-b border-surface-border pt-[env(safe-area-inset-top)]"
        >
        <div className="h-14 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-2 lg:hidden">
            <button
              type="button"
              data-testid="shell-mobile-menu-btn"
              aria-label="Abrir menu"
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 -ml-2 rounded-md text-ink-primary hover:bg-surface-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-primary/40"
            >
              <Menu size={22} strokeWidth={1.75} />
            </button>
            {/* Revisão H.2 — mesmo tamanho visual (size=20); padding só
                alarga o alvo clicável para ~44px de altura (header é h-14
                = 56px, comporta a folga). */}
            <button
              type="button"
              data-testid={SHELL.logoHomeMobileHeader}
              aria-label={homeLabel}
              onClick={goToDashboard}
              className="p-2.5 rounded-[6px] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-primary/40"
            >
              <AltarodaLogo variant="dark" size={20} />
            </button>
          </div>
          <div className="hidden lg:block text-sm text-ink-secondary">
            {new Date().toLocaleDateString("pt-PT", {
              weekday: "long",
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </div>
          <div className="flex items-center gap-1.5 min-w-0">
          {/* P0.6 — "+ Pedido" no modo operador. Fica no header e não num
              FAB para estar presente em todas as superfícies (Hoje, Agenda,
              Avisos) sem nunca tapar conteúdo. O rótulo encolhe para "+" em
              ecrãs estreitos; o alvo de toque mantém-se em 44px. */}
          {showHeaderNewRequest && (
            <button
              type="button"
              data-testid={SHELL.newRequest}
              aria-label="Novo pedido"
              onClick={goToNewRequest}
              className="inline-flex items-center gap-1.5 h-11 px-3 rounded-[10px] text-sm font-semibold text-white shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-primary/40"
              style={{ backgroundColor: "#CD013A" }}
            >
              <Plus size={18} strokeWidth={2.25} />
              <span className="hidden min-[360px]:inline">Pedido</span>
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={SHELL.userMenu}
                className="inline-flex items-center gap-2 rounded-[10px] px-2 py-1.5 hover:bg-surface-bg"
              >
                <div className="w-7 h-7 rounded-full bg-ink-primary text-white flex items-center justify-center text-xs font-semibold uppercase">
                  {user?.name?.slice(0, 1) || "U"}
                </div>
                <span className="hidden sm:block text-sm font-medium">
                  {user?.name}
                </span>
                <ChevronDown size={16} strokeWidth={1.5} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm">{user?.name}</div>
                <div className="text-xs text-ink-secondary">
                  {fmtRoleLabel(user?.role)} · @{user?.username}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => guardedNavigate("/perfil")}>
                <User size={16} strokeWidth={1.5} className="mr-2" />
                O meu perfil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => guardedNavigate("/change-password")}>
                <ShieldCheck size={16} strokeWidth={1.5} className="mr-2" />
                Alterar password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut size={16} strokeWidth={1.5} className="mr-2" />
                Terminar sessão
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
        </header>

        <main
          data-testid={SHELL.main}
          className={`${showFab ? "pb-nav-fab-safe" : "pb-nav-safe"} lg:pb-8 min-h-main-dvh`}
        >
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav — position: fixed anchored to the viewport (no
          transformed/overflow ancestor between here and the root, so this
          stays a true viewport-fixed element, never relative to a scroll
          container). `translateZ(0)` forces its own GPU compositor layer:
          without it, Android Chrome can visibly lag/clip a fixed bottom bar
          for a frame or more while the page repaints during scroll
          momentum and while the browser's own toolbar hides/shows. */}
      {bottomItems.length > 0 && (
      <nav
        data-testid={SHELL.bottomNav}
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-surface-border pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_12px_rgba(0,0,0,0.04)]"
        style={{ transform: "translateZ(0)" }}
      >
        <div className="flex h-16 items-stretch">
          {bottomItems.map((it) => (
            <BottomItem
              key={it.to}
              to={it.to}
              icon={it.icon}
              label={it.label}
              testId={it.testId}
              badge={it.badge === "unread" ? unread : undefined}
            />
          ))}
        </div>
      </nav>
      )}

      {/* Mobile Floating Action Button — atalho direto para "Novo pedido".
          Existe apenas uma ação, por isso não apresentamos menu intermédio.
          Quando forem adicionadas mais ações (ex.: Novo Lembrete), pode voltar
          a usar-se DropdownMenu — a estrutura fica preparada para essa
          extensão. Feedback tátil imediato: active:scale-95 + halo pulse. */}
      {showFab && (
        <div
          className="lg:hidden fixed z-40"
          style={{
            // max() keeps a comfortable 1rem margin from the edge on
            // ordinary screens, but grows to respect a device's safe-area
            // (curved-edge screens, landscape) instead of ever touching it.
            right: "max(1rem, env(safe-area-inset-right) + 1rem)",
            // M1 — subido ~10px face à bottom nav (era 4.5rem) para dar
            // mais respiro entre o FAB e a barra de navegação, sem tocar
            // na própria bottom nav.
            bottom: "calc(4.5rem + 10px + env(safe-area-inset-bottom))",
          }}
        >
          <button
            type="button"
            data-testid="mobile-fab"
            aria-label="Novo pedido"
            onClick={goToNewRequest}
            className="fab-btn relative w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg active:scale-95 transition-transform duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
            style={{ backgroundColor: "#CD013A" }}
          >
            <Plus size={26} strokeWidth={2} />
            {/* Halo pulse on active — pure CSS, no state. */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-full opacity-0 active:opacity-100 fab-ripple"
              style={{ boxShadow: "0 0 0 8px rgba(205,1,58,0.18)" }}
            />
          </button>
        </div>
      )}
    </div>
  );
}
